import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const localizer = momentLocalizer(moment);

const LeaveManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'calendar', 'analytics'
  
  // Data states
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    status: 'pending'
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    employee: 'all',
    leave_type: 'all',
    date_range: 'all'
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadLeaveRequests(),
        loadLeaveTypes(),
        loadEmployees(),
        loadStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveRequests = async () => {
    try {
      const response = await makeApiCall('api/leave/requests');
      if (response.ok) {
        const data = await response.json();
        setLeaveRequests(data.requests || []);
      } else {
        // Fallback to mock data
        setLeaveRequests(getMockLeaveRequests());
      }
    } catch (err) {
      console.error('Error loading leave requests:', err);
      setLeaveRequests(getMockLeaveRequests());
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const response = await makeApiCall('api/leave/types');
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data.leave_types || []);
      } else {
        setLeaveTypes(getMockLeaveTypes());
      }
    } catch (err) {
      console.error('Error loading leave types:', err);
      setLeaveTypes(getMockLeaveTypes());
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await makeApiCall('api/employees?page=1&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await makeApiCall('api/leave/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      } else {
        setStats(getMockStats());
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats(getMockStats());
    }
  };

  const getMockLeaveTypes = () => [
    { id: 1, leave_type_name: 'Vacation', is_paid: true, max_days_per_year: 15, color_code: '#4CAF50' },
    { id: 2, leave_type_name: 'Sick Leave', is_paid: true, max_days_per_year: 10, color_code: '#FF9800' },
    { id: 3, leave_type_name: 'Personal', is_paid: true, max_days_per_year: 5, color_code: '#2196F3' },
    { id: 4, leave_type_name: 'Bereavement', is_paid: true, max_days_per_year: 3, color_code: '#9C27B0' },
    { id: 5, leave_type_name: 'Jury Duty', is_paid: true, max_days_per_year: 10, color_code: '#607D8B' }
  ];

  const getMockLeaveRequests = () => [
    {
      id: 1,
      employee_id: 1,
      employee_name: 'John Doe',
      leave_type_id: 1,
      leave_type_name: 'Vacation',
      start_date: '2025-11-15',
      end_date: '2025-11-20',
      total_days: 5,
      reason: 'Family vacation',
      status: 'pending',
      requested_at: '2025-10-15T10:00:00Z',
      reviewed_by: null,
      reviewed_at: null,
      review_notes: null
    },
    {
      id: 2,
      employee_id: 2,
      employee_name: 'Jane Smith',
      leave_type_id: 2,
      leave_type_name: 'Sick Leave',
      start_date: '2025-10-20',
      end_date: '2025-10-20',
      total_days: 1,
      reason: 'Doctor appointment',
      status: 'approved',
      requested_at: '2025-10-19T14:30:00Z',
      reviewed_by: 1,
      reviewed_at: '2025-10-19T16:00:00Z',
      review_notes: 'Approved - medical appointment'
    },
    {
      id: 3,
      employee_id: 3,
      employee_name: 'Mike Johnson',
      leave_type_id: 1,
      leave_type_name: 'Vacation',
      start_date: '2025-12-23',
      end_date: '2025-12-31',
      total_days: 8,
      reason: 'Holiday vacation',
      status: 'rejected',
      requested_at: '2025-10-10T09:15:00Z',
      reviewed_by: 1,
      reviewed_at: '2025-10-11T11:30:00Z',
      review_notes: 'Rejected - too many people on vacation during holidays'
    }
  ];

  const getMockStats = () => ({
    total_requests: 25,
    pending_requests: 8,
    approved_requests: 15,
    rejected_requests: 2,
    total_days_requested: 45,
    average_days_per_request: 1.8,
    most_common_type: 'Vacation',
    upcoming_requests: 5
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckIcon />;
      case 'pending': return <PendingIcon />;
      case 'rejected': return <ErrorIcon />;
      default: return null;
    }
  };

  const handleCreateRequest = () => {
    setEditingRequest(null);
    setFormData({
      employee_id: '',
      leave_type_id: '',
      start_date: '',
      end_date: '',
      reason: '',
      status: 'pending'
    });
    setOpenDialog(true);
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setFormData({
      employee_id: request.employee_id,
      leave_type_id: request.leave_type_id,
      start_date: request.start_date,
      end_date: request.end_date,
      reason: request.reason,
      status: request.status
    });
    setOpenDialog(true);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
  };

  const handleReopenRequest = async (requestId) => {
    try {
      const response = await makeApiCall(`api/leave/requests/${requestId}/reopen`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setSuccess('Leave request reopened and returned to pending');
        setSelectedRequest(null);
        loadLeaveRequests();
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to reopen request');
      }
    } catch (err) {
      setError(err.message || 'Failed to reopen leave request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await makeApiCall(`api/leave/requests/${requestId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: 'Approved by HR' })
      });
      
      if (response.ok) {
        setSuccess('Leave request approved successfully');
        loadLeaveRequests();
      } else {
        throw new Error('Failed to approve request');
      }
    } catch (err) {
      setError('Failed to approve leave request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await makeApiCall(`api/leave/requests/${requestId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: 'Rejected by HR' })
      });
      
      if (response.ok) {
        setSuccess('Leave request rejected successfully');
        loadLeaveRequests();
      } else {
        throw new Error('Failed to reject request');
      }
    } catch (err) {
      setError('Failed to reject leave request');
    }
  };

  const handleSaveRequest = async () => {
    try {
      const url = editingRequest ? `api/leave/requests/${editingRequest.id}` : 'api/leave/requests';
      const method = editingRequest ? 'PUT' : 'POST';
      
      const response = await makeApiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSuccess(editingRequest ? 'Leave request updated successfully' : 'Leave request created successfully');
        setOpenDialog(false);
        loadLeaveRequests();
      } else {
        throw new Error('Failed to save request');
      }
    } catch (err) {
      setError('Failed to save leave request');
    }
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
        Leave Management
      </Typography>
      {/* TKT-202: removed the Calendar / Analytics toggle buttons until
          their views are actually implemented. The toggle was switching
          state but the render only knew about the List view, so users
          tapped Calendar/Analytics and nothing happened. List is the
          single canonical view for now and the leave-management stats
          cards already provide the high-level analytics summary. */}
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateRequest}
        >
          New Request
        </Button>
      </Box>
    </Box>
  );

  const renderStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.total_requests || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Requests
                </Typography>
              </Box>
              <CalendarIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.pending_requests || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pending
                </Typography>
              </Box>
              <PendingIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.approved_requests || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Approved
                </Typography>
              </Box>
              <CheckIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {stats.rejected_requests || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Rejected
                </Typography>
              </Box>
              <ErrorIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Leave Type</InputLabel>
              <Select
                value={filters.leave_type}
                onChange={(e) => setFilters({ ...filters, leave_type: e.target.value })}
                label="Leave Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {leaveTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.leave_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderRequestsList = () => {
    const filteredRequests = leaveRequests.filter(request => {
      if (filters.status !== 'all' && request.status !== filters.status) return false;
      if (filters.employee !== 'all' && request.employee_id !== parseInt(filters.employee)) return false;
      if (filters.leave_type !== 'all' && request.leave_type_id !== parseInt(filters.leave_type)) return false;
      return true;
    });

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Leave Requests
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {request.employee_name.charAt(0)}
                          </Avatar>
                          {request.employee_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.leave_type_name}
                          size="small"
                          sx={{ backgroundColor: leaveTypes.find(t => t.id === request.leave_type_id)?.color_code || '#ccc' }}
                        />
                      </TableCell>
                      <TableCell>{moment(request.start_date).format('MMM D, YYYY')}</TableCell>
                      <TableCell>{moment(request.end_date).format('MMM D, YYYY')}</TableCell>
                      <TableCell>{request.total_days}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(request.status)}
                          label={request.status.toUpperCase()}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{moment(request.requested_at).format('MMM D, YYYY')}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewRequest(request)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditRequest(request)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {request.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => handleApproveRequest(request.id)}>
                                  <ApproveIcon color="success" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" onClick={() => handleRejectRequest(request.id)}>
                                  <RejectIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const renderViewDialog = () => {
    if (!selectedRequest) return null;
    const leaveType = leaveTypes.find(t => t.id === selectedRequest.leave_type_id);
    return (
      <Dialog
        open={Boolean(selectedRequest)}
        onClose={() => setSelectedRequest(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="leave-view-dialog-title"
      >
        <DialogTitle id="leave-view-dialog-title">
          Leave Request Details
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar>{selectedRequest.employee_name?.charAt(0) || '?'}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedRequest.employee_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Requested {selectedRequest.requested_at ? moment(selectedRequest.requested_at).format('MMM D, YYYY h:mm A') : '-'}
                  </Typography>
                </Box>
              </Box>
              <Chip
                icon={getStatusIcon(selectedRequest.status)}
                label={(selectedRequest.status || '').toUpperCase()}
                color={getStatusColor(selectedRequest.status)}
              />
            </Box>
            <Divider />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Leave Type</Typography>
                <Box mt={0.5}>
                  <Chip
                    label={selectedRequest.leave_type_name || '-'}
                    size="small"
                    sx={{ backgroundColor: leaveType?.color_code || '#ccc' }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Total Days</Typography>
                <Typography variant="body1">{selectedRequest.total_days ?? '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Start Date</Typography>
                <Typography variant="body1">
                  {selectedRequest.start_date ? moment(selectedRequest.start_date).format('MMM D, YYYY') : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">End Date</Typography>
                <Typography variant="body1">
                  {selectedRequest.end_date ? moment(selectedRequest.end_date).format('MMM D, YYYY') : '-'}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="textSecondary">Reason</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRequest.reason || '-'}
                </Typography>
              </Grid>
              {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="textSecondary">Reviewed At</Typography>
                    <Typography variant="body1">
                      {selectedRequest.reviewed_at ? moment(selectedRequest.reviewed_at).format('MMM D, YYYY h:mm A') : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="textSecondary">Reviewer ID</Typography>
                    <Typography variant="body1">{selectedRequest.reviewed_by ?? '-'}</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="caption" color="textSecondary">Review Notes</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedRequest.review_notes || '-'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)} aria-label="Close leave request details">
            Close
          </Button>
          {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RefreshIcon />}
              onClick={() => handleReopenRequest(selectedRequest.id)}
              aria-label="Reopen this leave request and return it to pending"
            >
              Reopen as Pending
            </Button>
          )}
          {selectedRequest.status === 'pending' && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => {
                  handleRejectRequest(selectedRequest.id);
                  setSelectedRequest(null);
                }}
                aria-label="Reject this leave request"
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => {
                  handleApproveRequest(selectedRequest.id);
                  setSelectedRequest(null);
                }}
                aria-label="Approve this leave request"
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderRequestDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingRequest ? 'Edit Leave Request' : 'New Leave Request'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                label="Employee"
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Leave Type</InputLabel>
              <Select
                value={formData.leave_type_id}
                onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                label="Leave Type"
              >
                {leaveTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.leave_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveRequest}>
          {editingRequest ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderHeader()}
      {renderStats()}
      {renderFilters()}
      {renderRequestsList()}
      {renderRequestDialog()}
      {renderViewDialog()}
    </Box>
  );
};

export default LeaveManagement;
